import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getAllUsersWithRoles,
  setUserRole,
  ROLES,
  PERMISSIONS,
  hasPermission,
  getUserRole,
  getRoleById
} from '../utils/roleManager';

const { FiUsers, FiShield, FiEdit, FiSave, FiX, FiChevronUp, FiChevronDown, FiInfo, FiCheck, FiAlertTriangle } = FiIcons;

const RoleManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showRoleInfo, setShowRoleInfo] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    const usersWithRoles = getAllUsersWithRoles();
    setUsers(usersWithRoles);
  };

  const handleRoleChange = (username, newRoleId) => {
    const currentUserRole = getUserRole(currentUser);
    const currentUserRoleObj = getRoleById(currentUserRole);
    const targetRole = getRoleById(newRoleId);
    
    // Prevent users from promoting others to their level or higher
    if (targetRole.level >= currentUserRoleObj.level) {
      showNotification('You cannot assign a role equal to or higher than your own', 'error');
      return;
    }

    // Prevent users from modifying super admins
    const targetUserRole = getUserRole(username);
    const targetUserRoleObj = getRoleById(targetUserRole);
    if (targetUserRoleObj.level >= currentUserRoleObj.level) {
      showNotification('You cannot modify users with equal or higher roles', 'error');
      return;
    }

    if (setUserRole(username, newRoleId)) {
      loadUsers();
      setEditingUser(null);
      showNotification(`Successfully updated ${username}'s role to ${targetRole.name}`, 'success');
    } else {
      showNotification('Failed to update user role', 'error');
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const filteredUsers = users.filter(user => {
    if (filterRole === 'all') return true;
    return user.role.id === filterRole;
  });

  const getRoleColor = (roleId) => {
    const colors = {
      super_admin: 'text-red-400 bg-red-500/20 border-red-500/50',
      admin: 'text-purple-400 bg-purple-500/20 border-purple-500/50',
      moderator: 'text-blue-400 bg-blue-500/20 border-blue-500/50',
      premium_user: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50',
      user: 'text-green-400 bg-green-500/20 border-green-500/50',
      guest: 'text-gray-400 bg-gray-500/20 border-gray-500/50'
    };
    return colors[roleId] || colors.user;
  };

  const canModifyUser = (targetUser) => {
    const currentUserRole = getUserRole(currentUser);
    const currentUserRoleObj = getRoleById(currentUserRole);
    return targetUser.role.level < currentUserRoleObj.level;
  };

  const getAvailableRoles = () => {
    const currentUserRole = getUserRole(currentUser);
    const currentUserRoleObj = getRoleById(currentUserRole);
    
    return Object.values(ROLES).filter(role => 
      role.level < currentUserRoleObj.level
    );
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg border ${
              notification.type === 'success'
                ? 'bg-green-500/20 border-green-500/50 text-green-300'
                : 'bg-red-500/20 border-red-500/50 text-red-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <SafeIcon icon={notification.type === 'success' ? FiCheck : FiAlertTriangle} />
              {notification.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="text-blue-400" />
            Role Management
          </h2>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            {Object.values(ROLES).map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>

        {/* Role Legend */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {Object.values(ROLES).map(role => (
            <motion.div
              key={role.id}
              whileHover={{ scale: 1.02 }}
              className={`p-3 rounded-lg border cursor-pointer ${getRoleColor(role.id)}`}
              onClick={() => setShowRoleInfo(showRoleInfo === role.id ? null : role.id)}
            >
              <div className="text-center">
                <div className="font-semibold text-sm">{role.name}</div>
                <div className="text-xs opacity-80">Level {role.level}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Role Info Modal */}
        <AnimatePresence>
          {showRoleInfo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
              onClick={() => setShowRoleInfo(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
              >
                {(() => {
                  const role = getRoleById(showRoleInfo);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-white">{role.name}</h3>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowRoleInfo(null)}
                          className="text-gray-400 hover:text-white"
                        >
                          <SafeIcon icon={FiX} />
                        </motion.button>
                      </div>
                      <div className="space-y-3">
                        <p className="text-gray-300">{role.description}</p>
                        <div>
                          <h4 className="text-white font-semibold mb-2">Permissions:</h4>
                          <div className="space-y-1">
                            {role.permissions.includes('*') ? (
                              <span className="text-red-400 text-sm">All Permissions</span>
                            ) : (
                              role.permissions.map(permission => (
                                <div key={permission} className="text-sm text-gray-300">
                                  • {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-400">
                          Authority Level: {role.level}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/20">
                <th className="pb-3 text-white font-semibold">User</th>
                <th className="pb-3 text-white font-semibold">Current Role</th>
                <th className="pb-3 text-white font-semibold">Rating</th>
                <th className="pb-3 text-white font-semibold">Games</th>
                <th className="pb-3 text-white font-semibold">Last Active</th>
                <th className="pb-3 text-white font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <motion.tr
                  key={user.username}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-b border-white/10"
                >
                  <td className="py-3 text-white font-medium">
                    {user.username}
                    {user.username === currentUser && (
                      <span className="text-blue-400 text-sm ml-2">(You)</span>
                    )}
                  </td>
                  <td className="py-3">
                    <span className={`px-3 py-1 rounded-lg text-sm font-semibold border ${getRoleColor(user.role.id)}`}>
                      {user.role.name}
                    </span>
                  </td>
                  <td className="py-3 text-yellow-400">{user.stats?.rating || 1200}</td>
                  <td className="py-3 text-gray-300">{user.stats?.gamesPlayed || 0}</td>
                  <td className="py-3 text-gray-300">
                    {user.lastActive 
                      ? new Date(user.lastActive).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="py-3">
                    {canModifyUser(user) && user.username !== currentUser ? (
                      editingUser === user.username ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                          >
                            <option value="">Select Role</option>
                            {getAvailableRoles().map(role => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              if (selectedRole) {
                                handleRoleChange(user.username, selectedRole);
                              }
                            }}
                            className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                          >
                            <SafeIcon icon={FiSave} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setEditingUser(null);
                              setSelectedRole('');
                            }}
                            className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
                          >
                            <SafeIcon icon={FiX} />
                          </motion.button>
                        </div>
                      ) : (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditingUser(user.username);
                            setSelectedRole(user.role.id);
                          }}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold transition-colors"
                        >
                          <SafeIcon icon={FiEdit} />
                        </motion.button>
                      )
                    ) : (
                      <span className="text-gray-500 text-sm">
                        {user.username === currentUser ? 'Self' : 'Protected'}
                      </span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No users found with the selected role filter.
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;